/*
 * PROD-7828 R6 — pure request builders for the eleven /penalties endpoints. Each returns EXACTLY the
 * fields its handler reads (request_fields.test.js checks the keys against penalty_enums.json, CD-139);
 * an optional field goes out as null / "" / false rather than being dropped.
 */
import {
  BULK_ACTION,
  DIRECTIVE_ACTION,
  DIRECTIVE_CHOICE,
  LIMITS,
  MANUAL_TYPE,
  parse_number
} from './params';

const trimmed = (v) => String(v === null || v === undefined ? '' : v).trim();

// The month the shell shows is already the wire's "YYYY-MM"; a stray "-01" suffix is dropped.
export const wire_month = (value) => {
  const m = trimmed(value).match(/^(\d{4}-\d{2})/);
  return m ? m[1] : '';
};

// ---- reads ----

// filters: { teacher_uuid, penalty_type, waiver_status, source, overridden, orphaned } ("" = all).
export const list_params = (
  pay_month,
  filters = {},
  after_id = null,
  limit = LIMITS.LIST_LIMIT_DEFAULT
) => ({
  pay_month: wire_month(pay_month),
  teacher_uuid: trimmed(filters.teacher_uuid),
  penalty_type: trimmed(filters.penalty_type),
  waiver_status: trimmed(filters.waiver_status),
  source: trimmed(filters.source),
  overridden: trimmed(filters.overridden),
  orphaned: trimmed(filters.orphaned),
  after_id: after_id === null || after_id === undefined ? '' : String(after_id),
  limit: String(
    Math.min(Math.max(Number(limit) || LIMITS.LIST_LIMIT_DEFAULT, 1), LIMITS.LIST_LIMIT_MAX)
  )
});

export const summary_params = (pay_month) => ({ pay_month: wire_month(pay_month) });

export const session_params = (session_uuid) => ({ session_uuid: trimmed(session_uuid) });

export const free_pass_params = (teacher_uuid, pay_month) => ({
  teacher_uuid: trimmed(teacher_uuid),
  pay_month: wire_month(pay_month)
});

// ---- row writes (penalty_uuid + version from the row as loaded: the server's optimistic check) ----

const number_or_null = (raw) => parse_number(raw);

/*
 * D81 correction. form: { penalty_type, penalty_pct, amount, reason_code, note, confirm_large }. An unchanged type goes
 * as null; pct and amount go as typed (the form fills one, the server derives the other; both must agree
 * within PCT_AMOUNT_EPSILON when both are sent).
 */
export const correct_payload = (row, form) => ({
  penalty_uuid: row.uuid,
  version: row.version,
  penalty_type:
    form.penalty_type && form.penalty_type !== row.penalty_type ? form.penalty_type : null,
  penalty_pct: number_or_null(form.penalty_pct),
  amount: number_or_null(form.amount),
  reason_code: trimmed(form.reason_code),
  note: trimmed(form.note),
  // CD-190: a month-level correction at or above the confirm threshold is resent with this set.
  confirm_large: form.confirm_large === true
});

// waive=false un-waives; approved_by is a D91 label on a waive only.
export const waive_payload = (row, { waive, reason_code, note, approved_by }) => ({
  penalty_uuid: row.uuid,
  version: row.version,
  waive: waive === true,
  reason_code: trimmed(reason_code),
  note: trimmed(note),
  approved_by: waive === true ? trimmed(approved_by) : ''
});

export const revert_payload = (row, note) => ({
  penalty_uuid: row.uuid,
  version: row.version,
  note: trimmed(note)
});

export const remove_payload = (row, note) => ({
  penalty_uuid: row.uuid,
  version: row.version,
  note: trimmed(note)
});

/*
 * form: { client_request_id, teacher_uuid, session_uuid, penalty_type, penalty_pct, amount, reason_code,
 * note }. A MANUAL (month-level) penalty has no session; a session-linked one names the shell's month too,
 * which the server checks against the session's own month (CD-179). confirms come from the confirm step.
 */
export const add_payload = (pay_month, form, confirms = {}) => {
  const month_level = form.penalty_type === MANUAL_TYPE;
  return {
    client_request_id: trimmed(form.client_request_id),
    teacher_uuid: trimmed(form.teacher_uuid),
    session_uuid: month_level ? '' : trimmed(form.session_uuid),
    pay_month: wire_month(pay_month),
    penalty_type: trimmed(form.penalty_type),
    penalty_pct: month_level ? null : number_or_null(form.penalty_pct),
    amount: number_or_null(form.amount),
    reason_code: trimmed(form.reason_code),
    note: trimmed(form.note),
    confirm_large: confirms.confirm_large === true,
    confirm_duplicate: confirms.confirm_duplicate === true
  };
};

// ---- the free-pass directive (D92): expected = the directive exactly as the client read it ----

const sorted_days = (days) => (Array.isArray(days) ? [...days].sort() : []);

export const expected_directive = (state) => ({
  auto: !!(state && state.auto),
  pins: sorted_days(state && state.pins),
  skips: sorted_days(state && state.skips)
});

// req: { action, day, choice, move_to, note }; a field the action does not use goes as "".
export const free_pass_payload = (state, req, confirm_displace = false) => {
  const remove = req.action === DIRECTIVE_ACTION.REMOVE;
  const move = remove && req.choice === DIRECTIVE_CHOICE.MOVE;
  return {
    teacher_uuid: state.teacher_uuid,
    pay_month: wire_month(state.pay_month),
    expected: expected_directive(state),
    action: req.action,
    day: req.action === DIRECTIVE_ACTION.RESET_AUTO ? '' : trimmed(req.day),
    choice: remove ? trimmed(req.choice) : '',
    move_to: move ? trimmed(req.move_to) : '',
    confirm_displace: confirm_displace === true,
    note: trimmed(req.note)
  };
};

// ---- bulk ----

// rows: the selected wire rows (uuid + version). approved_by is a waive-only label (D91).
export const bulk_payload = (action, rows, form, confirm_displace = false) => ({
  action,
  items: rows.map((r) => ({ penalty_uuid: r.uuid, version: r.version })),
  reason_code: trimmed(form.reason_code),
  note: trimmed(form.note),
  approved_by: action === BULK_ACTION.WAIVE ? trimmed(form.approved_by) : '',
  confirm_displace: confirm_displace === true
});

const group_key = (r) => `${r.teacher_uuid}|${wire_month(r.pay_month)}`;

/*
 * The server makes each (teacher, month) group atomic, but only within one request. Chunks therefore
 * keep a group whole and pack groups up to the item cap, in (month, teacher) order like the server's; a
 * group larger than the cap alone is split, and only then can it half-apply.
 */
export const bulk_chunks = (rows, cap = LIMITS.BULK_MAX_ITEMS) => {
  const groups = new Map();
  rows.forEach((r) => {
    const k = group_key(r);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  });
  const ordered = [...groups.values()].sort((a, b) => {
    const ka = `${wire_month(a[0].pay_month)}|${a[0].teacher_uuid}`;
    const kb = `${wire_month(b[0].pay_month)}|${b[0].teacher_uuid}`;
    if (ka < kb) return -1;
    return ka > kb ? 1 : 0;
  });
  const chunks = [];
  let current = [];
  ordered.forEach((group) => {
    for (let i = 0; i < group.length; i += cap) {
      const part = group.slice(i, i + cap);
      if (current.length + part.length > cap) {
        chunks.push(current);
        current = [];
      }
      current = current.concat(part);
    }
  });
  if (current.length) chunks.push(current);
  return chunks;
};
